import React, { useState, useEffect, useMemo } from 'react';
import { Card, Btn, Modal, Input, Select, Table, SectionHeader, StatCard, Badge, HeaderBack } from '../components/UI';
import { fmtDate, fmt, fmtNum, exportCSV } from '../utils';
import * as api from '../api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DMRTransactionModal from '../components/DMRTransactionModal';

export default function ExpenseMasterDMRPage({ toast }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get('admin') === 'true';

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [subsections, setSubsections] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [boqItems, setBoqItems] = useState([]);

  // New Master Data
  const [suppliers, setSuppliers] = useState([]);
  const [accountLedgers, setAccountLedgers] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [costCategories, setCostCategories] = useState([]);

  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [successModal, setSuccessModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterCat, setFilterCat] = useState('');

  const [selectedTxnIds, setSelectedTxnIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadMasterData = async () => {
    setLoading(true);
    try {
      const [projRes, catRes, matRes, supRes, ledgRes, expRes, costCatRes] = await Promise.all([
        api.listProjects().catch(() => []),
        api.listDmrCategories().catch(() => []),
        api.listMaterials().catch(() => []),
        api.listSuppliers().catch(() => []),
        api.listAccountLedgers().catch(() => []),
        api.listExpenseCategories().catch(() => []),
        api.listCategories(false).catch(() => [])
      ]);
      setProjects(Array.isArray(projRes) ? projRes : (projRes?.data || projRes?.items || []));
      setCategories(Array.isArray(catRes) ? catRes : (catRes?.data || catRes?.items || []));
      setMaterials(Array.isArray(matRes) ? matRes : (matRes?.data || matRes?.items || []));
      setSuppliers(Array.isArray(supRes) ? supRes.filter(s => s.status === 'active') : []);
      setAccountLedgers(Array.isArray(ledgRes) ? ledgRes : []);
      setExpenseCategories(Array.isArray(expRes) ? expRes : []);
      setCostCategories(Array.isArray(costCatRes) ? costCatRes : []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const loadProjectSpecificData = async () => {
    if (!filterProject) {
      setBoqItems([]);
      setSubsections([]);
      return;
    }
    try {
      const [subsRes, boqRes] = await Promise.all([
        api.listDmrSubsections(filterProject).catch(() => []),
        api.listBOQ(filterProject).catch(() => [])
      ]);
      setSubsections(Array.isArray(subsRes) ? subsRes : (subsRes?.data || subsRes?.items || []));
      setBoqItems(Array.isArray(boqRes) ? boqRes : (boqRes?.data || boqRes?.items || []));
    } catch (e) {
      console.error(e);
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const txnRes = await api.listAllMaterialTxns(filterProject).catch(() => []);
      const txns = Array.isArray(txnRes) ? txnRes : (txnRes?.data || txnRes?.items || []);
      setTransactions(txns);

      if (!filterProject) {
        // Fetch BOQ items for all unique projects present in the global transaction list
        const uniqueProjectIds = [...new Set(txns.map(t => t.project_id).filter(Boolean))];
        if (uniqueProjectIds.length > 0) {
          const boqPromises = uniqueProjectIds.map(pid => api.listBOQ(pid).catch(() => []));
          const boqResults = await Promise.all(boqPromises);
          let allBoqs = [];
          boqResults.forEach(res => {
            const arr = Array.isArray(res) ? res : (res?.data || res?.items || []);
            allBoqs = [...allBoqs, ...arr];
          });
          setBoqItems(allBoqs);
        } else {
          setBoqItems([]);
        }
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    loadProjectSpecificData();
    loadTransactions();
  }, [filterProject]);

  const handleDeleteTransaction = async (id) => {
    setDeleteModal(id);
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    try {
      await api.deleteMaterialTxn(deleteModal);
      toast.show("Transaction deleted successfully", "success");
      loadTransactions();
    } catch (e) {
      toast.show(e.message, "error");
    }
    setDeleteModal(null);
  };

  const getMaterialName = (id) => materials.find(m => m.material_id === id)?.name || id;
  const getSubName = (id) => subsections.find(s => s.id === id)?.name || id;
  const getCatName = (id) => categories.find(c => c.id === id)?.name || id;
  const getSupplierName = (id) => suppliers.find(s => s.supplier_id === id)?.name || id;
  const getBoqMainName = (id) => {
    if (!id) return '—';
    const itemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);
    const item = itemsArray.find(b => String(b.boq_item_id) === String(id) || String(b.id) === String(id));
    return item ? (item.description || item.boq_code || item.item_code || item.code || id) : (id.length === 36 ? '—' : id);
  };
  const getBoqCompName = (boqId, compId) => {
    if (!compId) return '—';
    const itemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);
    const item = itemsArray.find(b => String(b.boq_item_id) === String(boqId) || String(b.id) === String(boqId));
    if (item && item.components) {
      const compsArray = Array.isArray(item.components) ? item.components : (item.components?.data || item.components?.items || []);
      const comp = compsArray.find(c => String(c.component_id) === String(compId) || String(c.id) === String(compId));
      if (comp) return comp.description || comp.component_code || comp.code || comp.name || compId;
    }
    return compId.length === 36 ? '—' : compId;
  };
  const getCostCatName = (id) => costCategories.find(c => c.cost_category_id === id)?.name || id;

  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      if (filterStatus && t.status !== filterStatus) return false;
      if (filterCat && t.dmr_category_id !== filterCat) return false;
      if (search) {
        const q = search.toLowerCase();
        const mat = getMaterialName(t.material_id).toLowerCase();
        const tid = (t.transaction_id || '').toLowerCase();
        const desc = (t.description || '').toLowerCase();
        if (!mat.includes(q) && !tid.includes(q) && !desc.includes(q)) return false;
      }
      return true;
    });
  }, [transactions, search, filterStatus, filterCat, materials]);

  useEffect(() => {
    setPage(1);
  }, [search, filterStatus, filterProject, filterCat]);

  const paginatedTransactions = useMemo(() => {
    if (pageSize === 'All') return filteredTransactions;
    const start = (page - 1) * pageSize;
    return filteredTransactions.slice(start, start + pageSize);
  }, [filteredTransactions, page, pageSize]);

  const stats = useMemo(() => {
    let totalValue = 0;
    filteredTransactions.forEach(t => {
      totalValue += parseFloat(t.total_amount || t.total_value || 0);
    });
    return { count: filteredTransactions.length, totalValue };
  }, [filteredTransactions]);

  const handleSelectTxn = (id) => {
    setSelectedTxnIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const draftIds = filteredTransactions.filter(t => t.status === 'DRAFT').map(t => t.transaction_id);
      setSelectedTxnIds(new Set([...selectedTxnIds, ...draftIds]));
    } else {
      const n = new Set(selectedTxnIds);
      filteredTransactions.forEach(t => n.delete(t.transaction_id));
      setSelectedTxnIds(n);
    }
  };

  const areAllDraftsSelected = useMemo(() => {
    const drafts = filteredTransactions.filter(t => t.status === 'DRAFT');
    if (drafts.length === 0) return false;
    return drafts.every(t => selectedTxnIds.has(t.transaction_id));
  }, [filteredTransactions, selectedTxnIds]);

  const submitSelected = async () => {
    const idsToSubmit = Array.from(selectedTxnIds);
    if (!idsToSubmit.length) return toast.show('Select at least one transaction', 'error');
    setSubmitting(true);
    try {
      await api.batchSubmitMaterialTxns({ transaction_ids: idsToSubmit });
      toast.show(`${idsToSubmit.length} transactions submitted`);
      setSelectedTxnIds(new Set());
      loadTransactions();
    } catch (e) { toast.show(e.message, 'error'); }
    setSubmitting(false);
  };

  return (
    <div className="fade-in">
      {/* Back button moved to top header */}
      <HeaderBack onClick={() => navigate('/expense-master')} label="Back to Expense Master" />

      {/* DASHBOARD STATS */}
      <div className="grid-cols-2" style={{ gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Transactions" value={stats.count} color="accent" />
        <StatCard label="Total Value" value={fmt(stats.totalValue)} color="cyan" />
      </div>

      <div className="flex-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Expense Register</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" onClick={() => exportCSV(filteredTransactions, 'dmr-entries')}>⬇ CSV</Btn>
          {!isAdmin && <Btn onClick={() => setCreateModal(true)}>Add Entry</Btn>}
        </div>
      </div>

      {/* ROBUST FILTER BAR */}
      <Card style={{ padding: '20px 24px', marginBottom: 24, borderRadius: 12, border: '1px solid black' }}>
        <div className="flex-mobile-col" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 16 }}>
          <div className="flex-mobile-col mobile-w-full" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Search Entries</div>
            <div className="mobile-w-full" style={{ flex: 1 }}>
              <Input placeholder="Search by name, ID, or description..." value={search} onChange={v => setSearch(v)} />
            </div>
          </div>
          <div className="flex-mobile-col mobile-w-full" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Sort By</span>
            <Select value="" onChange={() => { }} options={[{ value: '', label: 'Date Added' }]} style={{ minWidth: 140 }} />
            <Btn variant="secondary" onClick={() => { setSearch(''); setFilterStatus(''); setFilterProject(''); setFilterCat(''); }} style={{ whiteSpace: 'nowrap' }}>Clear Filters</Btn>
          </div>
        </div>

        <div className="grid-cols-4" style={{ display: 'grid', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Status</div>
            <Select value={filterStatus} onChange={setFilterStatus} options={[{ value: '', label: 'All Statuses' }, { value: 'DRAFT', label: 'DRAFT' }, { value: 'SUBMITTED', label: 'SUBMITTED' }, { value: 'APPROVED', label: 'APPROVED' }, { value: 'REJECTED', label: 'REJECTED' }]} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Project</div>
            <Select value={filterProject} onChange={setFilterProject} options={[{ value: '', label: 'All Projects' }, ...projects.map(p => ({ value: p.project_id, label: p.project_name }))]} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>MEP Category</div>
            <Select value={filterCat} onChange={setFilterCat} options={[{ value: '', label: 'All Categories' }, ...categories.map(c => ({ value: c.id, label: c.name }))]} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Supplier</div>
            <Select value="" onChange={() => { }} options={[{ value: '', label: 'All Suppliers' }, ...suppliers.map(s => ({ value: s.supplier_id, label: s.name }))]} />
          </div>
        </div>
      </Card>

      {selectedTxnIds.size > 0 && (
        <div style={{ padding: '12px 16px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{selectedTxnIds.size} draft transaction(s) selected</span>
          <Btn onClick={submitSelected} loading={submitting}>Submit Selected</Btn>
        </div>
      )}

      <Card style={{ border: '1px solid black', overflow: 'hidden' }}>
        <Table loading={loading} maxHeight="calc(100vh - 320px)" cols={[
          { align: 'center', key: 'select', label: <input type="checkbox" checked={areAllDraftsSelected} onChange={handleSelectAll} disabled={!filteredTransactions.some(t => t.status === 'DRAFT')} />, render: (_, row) => (
              row.status === 'DRAFT' ? <input type="checkbox" checked={selectedTxnIds.has(row.transaction_id)} onChange={() => handleSelectTxn(row.transaction_id)} /> : null
            )
          },
          { align: 'center', key: 'line_type', label: 'LINE TYPE', render: (_, row) => (
              <span style={{
                fontSize: 10,
                color: row.boq_component_id ? '#7e22ce' : '#1d4ed8',
                background: row.boq_component_id ? '#f3e8ff' : '#eff6ff',
                padding: '3px 6px',
                borderRadius: '4px',
                whiteSpace: 'nowrap',
                fontWeight: 800
              }}>
                {row.boq_component_id ? 'SUB' : 'MAIN'}
              </span>
            )
          },
          { align: 'center', key: 'boq_item_id', label: 'MAIN BOQ CODE', render: (v, row) => <span style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{row.boq_item_description || row.boq_item_name || (v ? getBoqMainName(v) : '—')}</span> },
          { align: 'center', key: 'boq_component_id', label: 'SUB BOQ CODE', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.boq_component_description || row.boq_component_name || (v ? getBoqCompName(row.boq_item_id, v) : '—')}</span> },
          { align: 'center', key: 'cost_category_id', label: 'COST COMP', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v ? getCostCatName(v) : '—'}</span> },
          { align: 'center', key: 'description', label: 'DESCRIPTION', render: (v, row) => <span style={{ fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}>{v || getMaterialName(row.material_id)}</span> },
          { align: 'center', key: 'brand_name', label: 'BRAND', render: (v, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || row.brand || '—'}</span> },
          { align: 'center', key: 'unit_of_measure', label: 'UNIT', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || '—'}</span> },
          { align: 'center', key: 'boq_qty', label: 'BOQ QTY', render: () => <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>—</span> },
          { align: 'center', key: 'quantity', label: 'COMP QTY', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtNum(v)}</span> },
          { align: 'center', key: 'unit_price', label: 'UNIT PRICE', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v ? fmt(v) : '—'}</span> },
          { align: 'center', key: 'mat', label: 'MAT', render: (v, row) => <span style={{ fontSize: 11, color: 'var(--primary)', whiteSpace: 'nowrap' }}>{fmt(row.total_amount || row.total_value)}</span> },
          { align: 'center', key: 'cia', label: 'CIA', render: () => <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>—</span> },
          { align: 'center', key: 'trn', label: 'TRN', render: () => <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>—</span> },
          { align: 'center', key: 'eqt', label: 'EQT', render: () => <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>—</span> },
          { align: 'center', key: 'lab', label: 'LAB', render: () => <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>—</span> },
          { align: 'center', key: 'total_amount', label: 'TOTAL AMOUNT', render: (v, row) => <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{fmt(v || row.total_value)}</span> },
          { align: 'center', key: 'boq_budget', label: 'BOQ BUDGET', render: () => <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>—</span> },
          { align: 'center', key: 'remaining', label: 'REMAINING', render: () => <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>—</span> },
          { align: 'center', key: 'usage', label: 'USAGE %', render: () => <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>—</span> },
          { align: 'center', key: 'supplier_id', label: 'SUPPLIER', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v ? getSupplierName(v) : '—'}</span> },
          { align: 'center', key: 'invoice_no', label: 'INVOICE', render: v => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{v || '—'}</span> },
          { align: 'center', key: 'alt', label: 'ALT', render: (_, row) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{row.alternative_item_name ? 'Yes' : '—'}</span> },
          { align: 'center', key: 'budget_status', label: 'BUDGET STATUS', render: () => <Badge color="gray" style={{ whiteSpace: 'nowrap' }}>N/A</Badge> },
          { align: 'center', key: 'status', label: 'STATUS', render: v => {
              let color = 'blue';
              if (v === 'DRAFT') color = 'amber';
              if (v === 'APPROVED') color = 'green';
              if (v === 'REJECTED') color = 'red';
              return <Badge color={color} style={{ whiteSpace: 'nowrap' }}>{v}</Badge>;
            }
          },
          { align: 'center', key: 'actions', label: 'ACTIONS', render: (_, row) => (
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                <Btn variant="ghost" size="sm" onClick={() => setViewModal(row)} title="View" style={{ padding: '6px 8px', background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', transition: 'all 0.2s' }} onMouseEnter={e => {e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#0f172a';}} onMouseLeave={e => {e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#475569';}}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                </Btn>
                {row.status === 'DRAFT' && (
                  <>
                    <Btn variant="ghost" size="sm" onClick={() => setEditModal(row)} title="Edit" style={{ padding: '6px 8px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', transition: 'all 0.2s' }} onMouseEnter={e => {e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.color = '#1e3a8a';}} onMouseLeave={e => {e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#1d4ed8';}}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                    </Btn>
                    <Btn variant="ghost" size="sm" style={{ padding: '6px 8px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', transition: 'all 0.2s' }} onClick={() => handleDeleteTransaction(row.id)} title="Delete" onMouseEnter={e => {e.currentTarget.style.background = '#fecaca'; e.currentTarget.style.color = '#b91c1c';}} onMouseLeave={e => {e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#ef4444';}}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                    </Btn>
                  </>
                )}
              </div>
            )
          },
        ]} rows={paginatedTransactions} emptyText="No material entries." />

        {/* PAGINATION CONTROLS */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>Show</span>
            <Select value={pageSize} onChange={v => { setPageSize(v === 'All' ? 'All' : Number(v)); setPage(1); }} options={[
              { value: 10, label: '10' },
              { value: 25, label: '25' },
              { value: 50, label: '50' },
              { value: 100, label: '100' },
              { value: 'All', label: 'All' }
            ]} style={{ minWidth: 80 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>entries</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Showing {pageSize === 'All' || filteredTransactions.length === 0 ?
                filteredTransactions.length > 0 ? 1 : 0 :
                (page - 1) * pageSize + 1} to {
                pageSize === 'All' ? filteredTransactions.length : Math.min(page * pageSize, filteredTransactions.length)
              } of {filteredTransactions.length} entries
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn variant="secondary" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Btn>
              <Btn variant="secondary" disabled={pageSize === 'All' || page * pageSize >= filteredTransactions.length} onClick={() => setPage(p => p + 1)}>Next</Btn>
            </div>
          </div>
        </div>
      </Card>

      {/* TRANSACTION MODAL */}
      {(createModal || viewModal || editModal) && (
        <DMRTransactionModal
          viewOnly={!!viewModal}
          transaction={viewModal || editModal}
          onClose={() => { setCreateModal(false); setViewModal(null); setEditModal(null); }}
          onSuccess={(isEdit) => { setCreateModal(false); setViewModal(null); setEditModal(null); setSuccessModal(isEdit ? 'Entry updated successfully' : 'Entry created successfully'); loadTransactions(); }}
          projectId={filterProject || (viewModal || editModal)?.project_id}
          toast={toast}
          materials={materials}
          boqItems={boqItems}
          subsections={subsections}
          categories={categories}
          suppliers={suppliers}
          accountLedgers={accountLedgers}
          expenseCategories={expenseCategories}
          costCategories={costCategories}
        />
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteModal && (
        <Modal hideHeader width={380} onClose={() => setDeleteModal(null)}>
          <div style={{ padding: '24px 16px', textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#fef2f2', border: '1px solid #fca5a5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px'
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Delete Transaction</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: 14, color: 'var(--text-secondary)' }}>
              Are you sure you want to delete this draft transaction? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <Btn variant="secondary" style={{ flex: 1, padding: '10px 0' }} onClick={() => setDeleteModal(null)}>Cancel</Btn>
              <Btn style={{ flex: 1, padding: '10px 0', background: '#ef4444', borderColor: '#ef4444', color: '#fff' }} onClick={confirmDelete}>Delete</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* SUCCESS CONFIRMATION MODAL */}
      {successModal && (
        <Modal hideHeader width={340} onClose={() => setSuccessModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '20px 10px 10px 10px' }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', background: '#f0fdf4', border: '1px solid #bbf7d0',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>Success</h3>
            <p style={{ margin: '0 0 32px 0', fontSize: 14, color: 'var(--text-secondary)' }}>{typeof successModal === 'string' ? successModal : 'Entry processed successfully'}</p>
            <Btn style={{ width: '100%', padding: '12px 0', background: '#16a34a', border: 'none', color: '#fff', fontSize: 15 }} onClick={() => setSuccessModal(false)}>Continue</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
